import { DirectUploadMedia } from "libs/directUpload";
import { DeliveryZone } from "modules/common";
import { DeliveryCompany } from "modules/deliveryCompanies";
import { Driver } from "modules/drivers";
import { Reseller } from "modules/resellers";

import {
  ExpeditionConfirmationSource,
  OrderCallConfirmationResult,
  OrderMode,
  OrderPaymentMethod,
  OrderStatus,
  OrderType,
  OrderUnpackingStatus,
  ProblemNotesForAdmin
} from "./constants";

export interface Order {
  id: string;
  createdAt: string;
  deliveryAddressCity: string;
  deliveryAddressCountry: string;
  deliveryAddressNeighborhood: string;
  itemsCount: string;
  orderTotal: number;
  deliveryFee: number;
  resellerImageUrl: string;
  resellerName: string;
  status: OrderStatus;
  totalCommission: number;
  hasUnavailableItems: boolean;
  markedAsUnavailableAt: string | null;
  totalMargin: number;
  driverImageUrl: string;
  driverName: string;
  deliveryCompanyName: string | null;
  deliveryCompanyAvatarUrl: string | null;
  paymentMethod: OrderPaymentMethod;
  unpackingStatus?: OrderUnpackingStatus;
  quantityOfItemsToUnpack?: number;
  unpackedAt?: string;
  feedbackGrade?: number;
  problemNotesForAdmin: ProblemNotesForAdmin[];
  isExpedition: boolean;
  /** The reseller bought this order for themselves: no commission, no margin
   * and no payout — the commission came off the price instead. */
  isPersonalOrder: boolean;
  lastCallConfirmationResult?: OrderCallConfirmationResult | null;
  lastCallConfirmationAt?: string | null;
  /** WhatsApp assistant thread the order was placed in. Null when a reseller
   * placed it, or when it predates the link being recorded. */
  conversationId: string | null;
}

export interface OrdersFilterStateProps {
  type?: OrderType | null;
  gender?: string | null;
  unpackingStatus?: string | null;
  driverId?: string | null;
  createdFrom?: string | null;
  createdTo?: string | null;
  orderTotalFrom?: number | null;
  orderTotalTo?: number | null;
  deliveryZoneId?: string | null;
  statuses?: string[] | null;
  clientName?: string | null;
  clientPhone?: string | null;
  resellerName?: string | null;
  resellerPhone?: string | null;
  deliveryLocation?: string | null;
}

export enum OrdersFilterGenderValues {
  ALL = "All",
  MALE = "Male",
  FEMALE = "Female"
}

export interface OrderLocation {
  id: string;
  createdAt: string;
  updatedAt: string;
  country: string;
  city: string;
  neighborhood: string;
}

export interface OrderDetails {
  id: string;
  status: OrderStatus;
  mode: OrderMode;
  paymentMethod: OrderPaymentMethod;
  totalPriceWithoutDiscount: number;
  problemNotesForAdmin: ProblemNotesForAdmin[];
  totalDiscount: number;
  itemsDiscount: number;
  couponDiscount: number;
  couponDiscountPercentage: number;
  hasUnavailableItems: boolean;
  markedAsUnavailableAt: string | null;
  deliveryDate: string;
  deliveryFee: number;
  totalOrder: number;
  totalCommission: number;
  totalMargin: number;
  resellerId: string;
  deliveryAddressId: string;
  driverId: string | null;
  deliveryCompanyId: string | null;
  deliveryCompany: DeliveryCompany | null;
  driver: Driver;
  history: OrderHistory[];
  feedback?: OrderFeedback;
  isExpedition: boolean;
  returnReason: string | null;
  returnDescription: string | null;
  cancellationReason: string | null;
  createdAt: string;
  reseller: Reseller;
  deliveryAddress: OrderDeliveryAddress;
  isReselling: boolean;
  /** The reseller bought this order for themselves: no commission, no margin
   * and no payout — the commission came off the price instead. */
  isPersonalOrder: boolean;
  items: OrderProductItem[];
  unpackingStatus?: OrderUnpackingStatus;
  quantityOfItemsToUnpack?: number;
  unpackedAt?: string;
  lastCallConfirmationResult?: OrderCallConfirmationResult | null;
  lastCallConfirmationAt?: string | null;
  callConfirmations?: OrderCallConfirmation[];
  /** WhatsApp assistant thread the order was placed in. Null when a reseller
   * placed it, or when it predates the link being recorded. */
  conversationId?: string | null;
}

export interface OrderCallConfirmation {
  id: string;
  orderId: string;
  result: OrderCallConfirmationResult;
  method: string;
  comment?: string;
  adminId?: string;
  adminName?: string;
  createdAt: string;
}

export interface OrderProductItem {
  id: string;
  orderId: string;
  barcodeId: string;
  quantity: number;
  price: number;
  discountedPricePerItem: number;
  totalPricePerItem: number;
  discount: number;
  discountAmount: number;
  /** Taken off this item in place of the reseller's commission on a personal
   * order. Already included in the order's `itemsDiscount`. */
  personalDiscountAmount: number;
  suggestionDiscountAmount: number;
  commission: number;
  margin: number;
  isUnavailable: boolean;
  packGroupId: string | null;
  packId: string | null;
  packSlotId: string | null;
  packTitle: string | null;
  packSlotTitle: string | null;
  packPrice: number | null;
  packIsAvailable: boolean | null;
  isPackPriceCarrier: boolean;
  quantityOfNotDefective: number;
  quantityReplaced: number;
  quantityThatCannotBeReplaced: number;
  quantityOfItemsToUnpack?: number;
  quantityOfUnavailable: number;
  returnQuantity: number;
  returnedQuantity: number;
  wereAllReviewedForReturn: boolean;
  createdAt: string;
  updatedAt: string;
  barcode: {
    id: string;
    productVariantId: string;
    productVariant: OrderProductItemVariant;
    sizeAttributeValueId: string;
    value: string;
    availableItemsCount: number;
    sizeAttributeValue: {
      id: string;
      attributeId: string;
      value: string;
    };
  };
}

export interface OrderProductItemVariant {
  id: string;
  buyingPrice: string;
  colorAttributeValue: {
    id: string;
    attributeId: string;
    value: string;
  };
  commission: number;
  discount: number;
  discountedPrice: number;
  image: DirectUploadMedia | null;
  images: DirectUploadMedia[];
  merchantPrice: number;
  name: string;
  productId: string;
  standardPrice: number;
  sku: string;
}

export interface OrderFeedback {
  id: string;
  orderId: string;
  grade: number;
  comment: string;
  images: DirectUploadMedia[];
  createdAt: string;
}

export interface OrderProductVariant {
  id?: string;
  colorValue: string;
  commission: number;
  discount: number;
  image: DirectUploadMedia | null;
  items: Array<{
    barcodeId: string;
    sizeValue: string;
    quantity: number;
  }>;
  name: string;
  price: number;
  productVariantId: string;
  totalCommission: number;
  totalMargin: number;
}

export interface OrderDeliveryAddress {
  address: string;
  createdAt: string;
  deliveryZone: DeliveryZone;
  deliveryZoneId: string;
  fullName: string;
  id: string;
  instructions: string;
  phoneNumber: string;
  resellerId: string;
  updatedAt: string;
  zip: string;
}

export interface OrderHistory {
  id: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  explanation: string | null;
  orderId: string;
  mediaId?: string;
  mediaUrl?: string;
  confirmationSource?: ExpeditionConfirmationSource;
}

export interface CancelOrderFormValues {
  orderId: string;
  cancellationReason: string;
}
